import { useReducer, useRef } from "react";
import IcConnecting from "@/assets/images/ic_connecting.png";
import IcConnect from "@/assets/svg/ic_connect.svg?react";
import IcCloseCircle from "@/assets/svg/ic_close_circle.svg?react";
import "./connectModal.css";
import { useMicSwitcherContext } from "./context";
import MeetingHandler from "@/bussiness/MeetingHandler";
import { toast } from "react-toastify";
import IcMicrophone from "@/assets/svg/ic_microphone.svg?react";
import useClickOutSide from "@/utils/useClickOutSide";
import { createDeviceByCode, ICustomeDevcie } from "@/types/device";
import fridayReporter from "@/utils/friday";
import { formatCustomDeviceInfo } from "./util";

enum ConnectorState {
  Disabled = "disabled",
  Ready = "ready",
  Connecting = "connecting",
  Error = "error",
}

interface ConnectState {
  status: ConnectorState;
  code: string;
  guessCode: string;
  errorMsg: string;
}

const initialState: ConnectState = {
  status: ConnectorState.Disabled,
  code: "",
  guessCode: "",
  errorMsg: "",
};

interface UpdateStateAction {
  type: "updateState";
  payload: Partial<ConnectState>;
}

const connectReducer = (state: ConnectState, action: UpdateStateAction) => {
  switch (action.type) {
    case "updateState": {
      return { ...state, ...action.payload };
    }
    default:
      return state;
  }
};

function ConnectModal() {
  const [connectState, dispatch] = useReducer(connectReducer, initialState);
  const { status, code, guessCode, errorMsg } = connectState;
  const { state, dispatch: MicSwitcherDispatch } = useMicSwitcherContext();
  const domRef = useRef<HTMLDivElement>(null);

  const { deviceList } = state;

  useClickOutSide(domRef, () => {
    MicSwitcherDispatch({ type: "updateConnectModal", payload: false });
  });

  const handleInputCode = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (status === ConnectorState.Connecting) return;
    const code = e.target.value.toUpperCase();
    if (code.length === 0) {
      dispatch({ type: "updateState", payload: { code: "", guessCode: "" } });
      return;
    }
    const findCode = deviceList.find((device) => device.code?.startsWith(code));
    const guessCode = findCode?.code || "";
    const codeStatus =
      code.length === 6 || code.length === 8
        ? ConnectorState.Ready
        : ConnectorState.Disabled;
    dispatch({
      type: "updateState",
      payload: { status: codeStatus, code, guessCode, errorMsg: " " },
    });
  };

  const handleEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") return;
    if (status === ConnectorState.Connecting) return;
    const currentCode = e.currentTarget.value.toUpperCase();
    const guessCode = deviceList.find((device) =>
      device.code?.startsWith(currentCode)
    )?.code;
    if (!guessCode) {
      if (status === ConnectorState.Disabled) {
        console.log('输入格式不对，返回');
        return;
      }
      e.currentTarget.blur();
      handleConnect(currentCode, true);
    } else {
      e.currentTarget.blur();
      dispatch({
        type: "updateState",
        payload: {
          code: guessCode,
          guessCode,
          status: ConnectorState.Ready,
          errorMsg: "",
        },
      });
      handleConnect(guessCode, true);
    }
  };

  const handleConnect = async (
    code: string,
    skipStatusCheck: boolean = false
  ) => {
    if (status !== ConnectorState.Ready && !skipStatusCheck) return;
    const device = deviceList.find((device) => device.code === code);
    // if (!device) {
    //   dispatch({
    //     type: "updateState",
    //     payload: {
    //       status: ConnectorState.Error,
    //       errorMsg: "连接码错误，请重新输入",
    //     },
    //   });
    //   return;
    // } else {
    try {
      dispatch({
        type: "updateState",
        payload: { status: ConnectorState.Connecting, errorMsg: "" },
      });
      let newDevice = createDeviceByCode(code);
      if (device) newDevice = device;
      await MeetingHandler.switchDevice(newDevice);
      // 如果不在设备列表中，通过pinCode连接后会去获取设备名称，在这里更新设备名称信息
      if (!device) {
        const wifiInfo = await MeetingHandler.getCurrentWifiInfo();
        if (wifiInfo) {
          newDevice = formatCustomDeviceInfo(wifiInfo) as ICustomeDevcie;
        }
      }
      console.log('已切换到拾音', newDevice, device);
      toast.success(`已切换到"${newDevice.name}"拾音`, {
        icon: IcMicrophone,
      });
      fridayReporter.pushData({
        eventCode: "RecordPage_CutMic_NoDevice_LinkStatus",
        payload: {
          link_status: "success",
        },
      });
      MicSwitcherDispatch({ type: "updateConnectModal", payload: false });
      MicSwitcherDispatch({ type: "updateCurrentDevice", payload: newDevice });
    } catch (error) {
      fridayReporter.pushData({
        eventCode: "RecordPage_CutMic_NoDevice_LinkStatus",
        payload: {
          link_status: "fail",
        },
      });
      console.log("connect error", error);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const errMsg = (error as any)?.message || "连接失败"
      dispatch({
        type: "updateState",
        payload: { status: ConnectorState.Error, errorMsg: errMsg },
      });
    }
    // }
  };

  const handleCear = () => {
    dispatch({
      type: "updateState",
      payload: {
        code: "",
        guessCode: "",
        status: ConnectorState.Disabled,
        errorMsg: "",
      },
    });
  };

  const handleClickConnect = () => {
    handleConnect(code);
  };

  return (
    <div
      ref={domRef}
      className="w-60 h-42 position-absolute top--51  left-0 bg-mx-white rounded-2 shadow-mx"
    >
      <div className="m-t-10 text-center c-mx-secondary text-3.5 line-height-5">
        连接设备
      </div>
      <div
        className={`m-t-5.5 m-x-5 w-50 h-10 flex items-center rounded-full border border-solid ${status === ConnectorState.Error
          ? "border-mx-error"
          : "border-mx-quaternary"
          }`}
      >
        <div className="relative flex-1 h-full">
          {!code && !guessCode && (
            <input
              type="text"
              readOnly
              placeholder="请输入6/8位连接码"
              className="code-input placeholder-#CED2D9"
            />
          )}
          {guessCode && (
            <input
              type="text"
              readOnly
              value={guessCode}
              className="code-input tracking-1.5 c-#CED2D9"
            />
          )}
          <input
            type="text"
            className="code-input tracking-1.5 c-mx-primary focus:outline-none placeholder-#ccccc"
            spellCheck={false}
            maxLength={8}
            value={code}
            onChange={handleInputCode}
            onKeyUp={handleEnter}
            autoFocus
          />
        </div>
        {status === ConnectorState.Error && (
          <IcCloseCircle
            className="w-3 h-3 m-r-3 cursor-pointer"
            onClick={handleCear}
          />
        )}
        <div
          className={`w-6 h-6 min-w-6 m-r-1 rounded-full ${status === ConnectorState.Disabled
            ? "bg-mx-primary op-60"
            : status === ConnectorState.Ready ||
              status === ConnectorState.Connecting
              ? "bg-#4981F2 cursor-pointer"
              : status === ConnectorState.Error
                ? "bg-#FF584C"
                : ""
            }`}
          onClick={handleClickConnect}
        >
          {status === ConnectorState.Connecting ? (
            // 设计表示切不出来svg，只能用图片代替
            <img className="w-6 h-6 animate-spin" src={IcConnecting} />
          ) : (
            <IcConnect className="w-6 h-6" />
          )}
        </div>
      </div>
      {guessCode &&
        status !== ConnectorState.Connecting &&
        status !== ConnectorState.Error && (
          <div className="m-t-2 text-center text-#CED2D9 text-3 line-height-4.25">
            Enter快速补全连接码
          </div>
        )}
      {status === ConnectorState.Error && (
        <div className="m-t-2 text-center text-#FF584C text-3 line-height-4.25">
          {errorMsg}
        </div>
      )}
    </div>
  );
}

export default ConnectModal;
